import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useArtistStore } from '../../hooks/useArtistStore';
import type { MockArtist } from '../../hooks/useArtistStore';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { BottomNav } from '../../components/feature/BottomNav';
import { ArtistEvents } from './components/ArtistEvents';
import { detectEmbedType } from './components/EmbedPlayer';
import { LayerPlayer } from './components/LayerPlayer';
import { LayerCredits } from './components/LayerCredits';
import { LayerSocial } from './components/LayerSocial';
import { ArtistHeroMobile, ArtistHeroDesktop } from './components/ArtistHero';
import { useFavoriteCount } from '../../hooks/useFavoriteCount';
import { useAuth } from '../../hooks/useAuth';
import { useTracking } from '../../hooks/useTracking';
import { getLayerColors, shiftHue } from './utils/colorUtils';
import { VagueEchoForm } from '../../components/feature/VagueEchoForm';
import { useGlobalPlayer } from '../../hooks/useGlobalPlayer';
import { ResonateModal } from './components/ResonateModal';
import { ResonanceStrata } from './components/ResonanceStrata';
import { LevelUpOverlay } from './components/LevelUpOverlay';
import { ArtistEchoes } from './components/ArtistEchoes';
import { FanLevelProgress } from './components/FanLevelProgress';
import { useResonanceLevelUp } from '../../hooks/useResonanceLevelUp';
import { motion } from 'framer-motion';
import type {
  ArtistData,
  LayerData,
  SocialDisplayItem,
  CreditEntry,
  GenreAttribute,
  PlayerInfo,
} from './types';

// localStorageキー生成
function getDepthStorageKey(userId: string, artistId: string) {
  return `depth_lv_${userId}_${artistId}`;
}

export default function ArtistPage() {
  const { id, userId } = useParams<{ id?: string; userId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAutoplay = searchParams.get('autoplay') === 'true';
  const { artists: mockArtistsList } = useArtistStore();
  const { setTrack } = useGlobalPlayer();

  const [artist, setArtist] = useState<ArtistData | null>(null);
  const [layerData, setLayerData] = useState<LayerData | null>(null);
  const [mockArtistFull, setMockArtistFull] = useState<MockArtist | null>(null);
  const [bgOpacity, setBgOpacity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mockArtistId, setMockArtistId] = useState<number | null>(null);
  const [genresAndAttributes, setGenresAndAttributes] = useState<GenreAttribute[]>([]);
  const [artistIsPremium, setArtistIsPremium] = useState(false);
  const [isResonateModalOpen, setIsResonateModalOpen] = useState(false);
  const [strataRefreshTrigger, setStrataRefreshTrigger] = useState(0);
  const [depthDetails, setDepthDetails] = useState({
    fanLevel: 0,
    currentExp: 0,
    nextLevelExp: 10,
    checkInExp: 0,
    supportExp: 0,
    hasVeteranBonus: false,
  });
  const [patronCredits, setPatronCredits] = useState<CreditEntry[]>([]);
  const [showDepthBadgePopup, setShowDepthBadgePopup] = useState(false);
  const [copyDone, setCopyDone] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const { user } = useAuth();
  const {
    recordProfileView,
    recordLinkPlay,
    recordSnsClick,
    recordResonateClick,
    recordFragmentClick,
  } = useTracking();

  const isMockArtist = !!id && !userId;
  const isUserArtist = !!userId;

  const { count: favoriteCount, loading: countLoading } = useFavoriteCount({
    artistId: isMockArtist ? String(id) : userId || '',
    artistType: isMockArtist ? 'mock' : 'artist',
  });

  // プロフィール閲覧を記録
  useEffect(() => {
    if (!isUserArtist || !userId || userId.startsWith(':')) return;
    recordProfileView(userId, user?.id);
  }, [isUserArtist, userId, user, recordProfileView]);

  const handleSnsClick = useCallback(
    (platform: string) => {
      if (isUserArtist && userId && !userId.startsWith(':')) {
        recordSnsClick(userId, platform);
      }
    },
    [isUserArtist, userId, recordSnsClick]
  );

  const handleResonateClick = useCallback(() => {
    if (isUserArtist && userId && !userId.startsWith(':')) {
      recordResonateClick(userId);
    }
    setIsResonateModalOpen(true);
  }, [isUserArtist, userId, recordResonateClick]);

  const handleFragmentClick = useCallback(() => {
    if (isUserArtist && userId && !userId.startsWith(':')) {
      recordFragmentClick(userId);
    }
  }, [isUserArtist, userId, recordFragmentClick]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopyDone(true);
      setTimeout(() => {
        setCopyDone(false);
        setShareMenuOpen(false);
      }, 2000);
    });
  };

  // ジャンル・属性を取得する共通関数
  const fetchGenresAndAttributes = useCallback(async (targetUserId: string) => {
    const genreAttrs: GenreAttribute[] = [];
    const [mainGenresRes, mainPartsRes, genresRes, attributesRes] = await Promise.all([
      supabase.from('artist_main_genres').select('genre_name, color').eq('user_id', targetUserId),
      supabase.from('artist_main_parts').select('part_name, color').eq('user_id', targetUserId),
      supabase.from('artist_genres').select('genre_name, color').eq('user_id', targetUserId),
      supabase.from('artist_attributes').select('attribute_name, color').eq('user_id', targetUserId),
    ]);
    (mainGenresRes.data || []).forEach((g: { genre_name: string; color: string }) => {
      genreAttrs.push({ name: g.genre_name, type: 'main_genre', color: g.color });
    });
    (mainPartsRes.data || []).forEach((p: { part_name: string; color: string }) => {
      genreAttrs.push({ name: p.part_name, type: 'main_part', color: p.color });
    });
    (genresRes.data || []).forEach((g: { genre_name: string; color: string }) => {
      genreAttrs.push({ name: g.genre_name, type: 'genre', color: g.color });
    });
    (attributesRes.data || []).forEach((a: { attribute_name: string; color: string }) => {
      genreAttrs.push({ name: a.attribute_name, type: 'attribute', color: a.color });
    });
    return genreAttrs;
  }, []);

  // パトロンデータ取得
  useEffect(() => {
    const fetchPatrons = async () => {
      if (!isUserArtist || !userId || userId.startsWith(':')) {
        setPatronCredits([]);
        return;
      }
      try {
        const { data: payments, error } = await supabase
          .from('resonance_payments')
          .select('seeker_id, amount')
          .eq('artist_id', userId)
          .eq('status', 'succeeded');
        if (error) throw error;
        const supportTotals = new Map<string, number>();
        (payments || []).forEach((payment: { seeker_id: string; amount: number }) => {
          const current = supportTotals.get(payment.seeker_id) || 0;
          supportTotals.set(payment.seeker_id, current + payment.amount);
        });
        const patronEntries = Array.from(supportTotals.entries())
          .filter(([, total]) => total >= 10000)
          .sort((a, b) => b[1] - a[1]);
        const patronIds = patronEntries.map(([seekerId]) => seekerId);
        if (patronIds.length === 0) {
          setPatronCredits([]);
          return;
        }
        const [profilesRes, layersRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('id, display_name, prefecture')
            .in('id', patronIds),
          supabase
            .from('artist_layers')
            .select('user_id, profile_image_url')
            .in('user_id', patronIds),
        ]);
        if (profilesRes.error) throw profilesRes.error;
        const profileMap = new Map<string, { display_name: string; prefecture: string }>();
        (profilesRes.data || []).forEach((p: { id: string; display_name: string; prefecture: string }) => {
          profileMap.set(p.id, p);
        });
        const imageMap = new Map<string, string>();
        (layersRes.data || []).forEach((l: { user_id: string; profile_image_url: string }) => {
          if (l.profile_image_url) imageMap.set(l.user_id, l.profile_image_url);
        });
        const patrons: CreditEntry[] = patronEntries.map(([seekerId, total]) => {
          const profile = profileMap.get(seekerId);
          return {
            name: profile?.display_name || 'Anonymous Patron',
            genre: 'PATRON',
            location: profile?.prefecture?.toUpperCase() || '',
            description: '累計10,000pt以上の共鳴を果たした深淵の観測者。',
            imageUrl: imageMap.get(seekerId) || '',
            artistUserId: seekerId,
            snsUrl: '',
            collectiveType: '' as const,
            totalAmount: total,
          };
        });
        setPatronCredits(patrons);
      } catch (err) {
        console.error('Failed to fetch patrons:', err);
        setPatronCredits([]);
      }
    };
    fetchPatrons();
  }, [isUserArtist, userId]);

  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpNewLevel, setLevelUpNewLevel] = useState(1);

  // レベルアップフック（RESONATE後の即時検知用）
  const { levelUpInfo: resonateLevelUpInfo, checkLevelUp, clearLevelUp } = useResonanceLevelUp();

  // 個別アーティストのDEPTH詳細を取得
  useEffect(() => {
    const fetchDepthDetails = async () => {
      if (!user || !isUserArtist || !userId || userId.startsWith(':')) return;
      try {
        const { data, error } = await supabase.rpc('get_user_artist_depth_details', {
          p_user_id: user.id,
          p_artist_id: userId,
        });
        if (error) throw error;
        if (data) {
          const newLevel = data.fanLevel ?? 0;

          // レベルアップ検知
          const storageKey = getDepthStorageKey(user.id, userId);
          const savedLevel = parseInt(localStorage.getItem(storageKey) || '0', 10);
          if (newLevel > savedLevel && savedLevel >= 0 && newLevel > 0) {
            // 初回訪問（savedLevel===0）はアニメーションを出さない
            if (savedLevel > 0) {
              setLevelUpNewLevel(newLevel);
              setShowLevelUp(true);
            }
            localStorage.setItem(storageKey, String(newLevel));
          } else if (savedLevel === 0 && newLevel > 0) {
            // 初回記録
            localStorage.setItem(storageKey, String(newLevel));
          }

          setDepthDetails({
            fanLevel: newLevel,
            currentExp: data.currentExp ?? 0,
            nextLevelExp: data.nextLevelExp ?? 10,
            checkInExp: data.checkInExp ?? 0,
            supportExp: data.supportExp ?? 0,
            hasVeteranBonus: data.hasVeteranBonus ?? false,
          });
        }
      } catch (err) {
        console.error('Failed to fetch depth details:', err);
      }
    };
    fetchDepthDetails();
  }, [user, isUserArtist, userId]);

  // RESONATE成功後：strataリフレッシュ＋レベルアップ検知
  const handleResonateSuccess = useCallback(async () => {
    setStrataRefreshTrigger((prev) => prev + 1);
    if (user && isUserArtist && userId && !userId.startsWith(':') && artist) {
      await checkLevelUp(user.id, userId, artist.name, artist.color);
    }
  }, [user, isUserArtist, userId, artist, checkLevelUp]);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setMockArtistId(null);
      setMockArtistFull(null);
      setGenresAndAttributes([]);
      try {
        if (isMockArtist) {
          const foundArtist = mockArtistsList.find((a) => a.id === Number(id));
          if (foundArtist) {
            setMockArtistId(foundArtist.id);
            setMockArtistFull(foundArtist);
            setArtist({
              name: foundArtist.name,
              location: foundArtist.location,
              genre: foundArtist.genre,
              image: foundArtist.image,
              color: foundArtist.color,
              platforms: foundArtist.platforms,
            });
            if (userId) {
              const attrs = await fetchGenresAndAttributes(userId);
              setGenresAndAttributes(attrs);
            }
          } else {
            navigate('/');
            return;
          }
        } else if (isUserArtist) {
          const [layerRes, profileRes] = await Promise.all([
            supabase.from('artist_layers').select('*').eq('user_id', userId).maybeSingle(),
            supabase
              .from('profiles')
              .select('display_name, prefecture, is_premium')
              .eq('id', userId)
              .maybeSingle(),
          ]);
          if (layerRes.error) throw layerRes.error;
          const layer = layerRes.data;
          const profile = profileRes.data;
          setArtistIsPremium(profile?.is_premium || false);
          const resolvedName = profile?.display_name || layer?.artist_name || 'UNKNOWN';
          const resolvedLocation = profile?.prefecture
            ? profile.prefecture.toUpperCase()
            : (layer?.location || '').toUpperCase();
          if (layer) {
            setLayerData(layer as LayerData);
            setArtist({
              name: resolvedName,
              location: resolvedLocation,
              genre: layer.genre || '',
              image: layer.profile_image_url || '',
              color: layer.color || '#00ff41',
              platforms: [],
            });
            const attrs = await fetchGenresAndAttributes(userId);
            setGenresAndAttributes(attrs);
          } else if (profile) {
            setArtist({
              name: resolvedName,
              location: resolvedLocation,
              genre: '',
              image: '',
              color: '#00ff41',
              platforms: [],
            });
          } else {
            navigate('/');
            return;
          }
        } else {
          navigate('/');
          return;
        }
      } catch (err) {
        console.error('Failed to fetch artist:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, userId, isMockArtist, isUserArtist, navigate, mockArtistsList, fetchGenresAndAttributes]);

  // スクロールエフェクト
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const fadeEnd = window.innerHeight * 0.7;
    const opacity = Math.max(0, 1 - scrollY / fadeEnd);
    setBgOpacity(opacity);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Layer accent colors
  const layerColors = useMemo(() => {
    if (!artist) return { layer02: '#ff006e', layer03: '#00ff41', layer04: '#00d9ff' };
    return getLayerColors(artist.color);
  }, [artist]);

  // Player layer
  const hasPlayerLayer = useMemo((): boolean => {
    if (isUserArtist && layerData) return !!(layerData.track_name || layerData.player_sns_url);
    if (isMockArtist && mockArtistFull) return !!(mockArtistFull.playerData?.trackName);
    return false;
  }, [layerData, isUserArtist, isMockArtist, mockArtistFull]);

  const playerInfo = useMemo((): PlayerInfo | null => {
    if (isUserArtist && layerData && (layerData.track_name || layerData.player_sns_url)) {
      return {
        trackName: layerData.track_name || '',
        trackLabel: layerData.track_label || 'Track',
        duration: layerData.duration || '0:00',
        playerSnsUrl: layerData.player_sns_url || '',
        soundElements: layerData.sound_elements || null,
      };
    }
    if (isMockArtist && mockArtistFull?.playerData?.trackName) {
      return {
        trackName: mockArtistFull.playerData.trackName,
        trackLabel: mockArtistFull.playerData.trackLabel || 'Track',
        duration: mockArtistFull.playerData.duration || '0:00',
        playerSnsUrl: '',
        soundElements: null,
      };
    }
    return null;
  }, [layerData, isUserArtist, isMockArtist, mockArtistFull]);

  // グローバルプレイヤーへの登録
  const handleLinkPlay = useCallback(() => {
    if (isUserArtist && userId && !userId.startsWith(':')) {
      recordLinkPlay(userId);
    }
    if (!playerInfo || !artist) return;
    const snsUrl = playerInfo.playerSnsUrl || '';
    const embedType = snsUrl ? detectEmbedType(snsUrl) : 'none';
    setTrack({
      url: snsUrl,
      trackName: playerInfo.trackName || artist.name,
      artistName: artist.name,
      artistId: isMockArtist ? String(id) : userId || '',
      artistType: isMockArtist ? 'mock' : 'artist',
      accentColor: artist.color,
      embedType: embedType === 'none' && snsUrl ? 'link' : embedType,
    });
  }, [isUserArtist, userId, recordLinkPlay, playerInfo, artist, setTrack, isMockArtist, id]);

  // Credits
  const credits = useMemo((): CreditEntry[] => {
    let manualCredits: CreditEntry[] = [];
    if (isUserArtist && layerData?.credits?.length) {
      const filtered = layerData.credits.filter(
        (c: Record<string, string>) => c.name || c.genre || c.location
      );
      if (filtered.length > 0) {
        manualCredits = filtered.map((c: Record<string, string>) => ({
          name: c.name || '',
          genre: c.genre || '',
          location: c.location || '',
          description: c.description || '',
          imageUrl: c.imageUrl || '',
          artistUserId: c.artistUserId || '',
          snsUrl: c.snsUrl || '',
          collectiveType: (c.collectiveType as 'member' | 'support' | '') || '',
        }));
      }
    }
    if (isMockArtist && mockArtistFull?.creditsData?.length) {
      const filtered = mockArtistFull.creditsData.filter((c) => c.name || c.genre || c.location);
      if (filtered.length > 0) manualCredits = filtered;
    }
    return [...patronCredits, ...manualCredits];
  }, [layerData, isUserArtist, isMockArtist, mockArtistFull, patronCredits]);

  // Socials
  const socials = useMemo((): SocialDisplayItem[] => {
    if (isUserArtist && layerData) {
      if (Array.isArray(layerData.socials) && layerData.socials.length > 0) {
        const filled = layerData.socials.filter((s) => s.username || s.url);
        if (filled.length > 0) {
          return filled.map((s) => ({
            name: s.platform,
            icon: s.icon,
            username: s.username,
            url: s.url,
          }));
        }
      }
    }
    if (isMockArtist && mockArtistFull?.socialsData?.length) {
      const filled = mockArtistFull.socialsData.filter((s) => s.username || s.url);
      if (filled.length > 0) {
        return filled.map((s) => ({
          name: s.platform,
          icon: s.icon,
          username: s.username,
          url: s.url,
        }));
      }
    }
    return [];
  }, [layerData, isUserArtist, isMockArtist, mockArtistFull]);

  // OGP設定（動的）
  useEffect(() => {
    if (!artist) return;
    const title = `${artist.name} - DigDim`;
    const description = `${artist.name} | ${artist.genre} | ${artist.location} - アンダーグラウンド音楽発見プラットフォーム DigDim`;
    const imageUrl =
      artist.image ||
      'https://public.readdy.ai/ai/img_res/48bd0092a8b3f22e385156c9381030f2.jpg';
    document.title = title;
    const setMetaTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    const setMetaName = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:image', imageUrl);
    setMetaTag('og:type', 'profile');
    setMetaTag('og:url', window.location.href);
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', title);
    setMetaName('twitter:description', description);
    setMetaName('twitter:image', imageUrl);
    setMetaName('description', description);
    return () => {
      document.title = 'DigDim - アンダーグラウンド音楽発見プラットフォーム';
    };
  }, [artist]);

  if (loading || !artist) {
    return (
      <div className="bg-zinc-900 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line text-3xl text-brand-primary animate-spin" />
          <span className="text-zinc-500 text-sm tracking-wider">LOADING...</span>
        </div>
      </div>
    );
  }

  const c01 = artist.color;
  const c02 = layerColors.layer02;
  const c03 = layerColors.layer03;
  const c04 = layerColors.layer04;
  const cEvents = shiftHue(c01, 15);
  const cStrata = shiftHue(c04, 20);
  const cEchoes = shiftHue(c03, -15);

  const heroProps = {
    artist,
    accentColor: c01,
    genresAndAttributes,
    favoriteCount,
    countLoading,
    artistId: isMockArtist ? String(id) : (userId || ''),
    artistType: (isMockArtist ? 'mock' : 'artist') as 'mock' | 'artist',
    supportUrl:
      isUserArtist && artistIsPremium && layerData?.support_url ? layerData.support_url : undefined,
    storeUrl:
      isUserArtist && artistIsPremium && layerData?.store_url ? layerData.store_url : undefined,
    showPremiumBanner: isUserArtist && !artistIsPremium && user?.id === userId,
    onResonateClick: handleResonateClick,
    onFragmentClick: handleFragmentClick,
    fanLevel: depthDetails.fanLevel,
    currentExp: depthDetails.currentExp,
    nextLevelExp: depthDetails.nextLevelExp,
    checkInExp: depthDetails.checkInExp,
    supportExp: depthDetails.supportExp,
    hasVeteranBonus: depthDetails.hasVeteranBonus,
  };

  return (
    <div className="min-h-screen bg-zinc-900 relative overflow-hidden">
      {/* Background effects */}
      <div
        className="fixed inset-0 opacity-[0.015] pointer-events-none z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E")`,
        }}
      />

      {/* Layer connecting vertical line (desktop only) */}
      <div className="hidden lg:block fixed left-1/2 top-0 bottom-0 w-px pointer-events-none z-[5]">
        <div
          className="absolute left-0 top-0 bottom-0 w-full opacity-20"
          style={{
            background: `linear-gradient(to bottom, ${c01}00, ${c01}, ${c02}, ${c03}, ${c04}, ${c04}00)`,
          }}
        />
      </div>

      {/* Back Button */}
      <div className="fixed top-8 left-8 z-50">
        <motion.button
          onClick={() => navigate(-1)}
          whileTap={{ scale: 0.98 }}
          className="px-6 py-3 bg-zinc-800/80 backdrop-blur-sm border border-zinc-700/50 rounded-full text-zinc-100 font-bold tracking-wider hover:bg-zinc-700/50 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
          style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}
        >
          <i className="ri-arrow-left-line" />
          BACK
        </motion.button>
      </div>

      {/* SNS Share Button（中央・固定） */}
      {!loading && artist && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50">
          {/* 共有ボタン */}
          <motion.button
            onClick={() => setShareMenuOpen((prev) => !prev)}
            whileTap={{ scale: 0.98 }}
            className="w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 cursor-pointer whitespace-nowrap"
            style={{
              background: shareMenuOpen ? `${artist.color}25` : 'rgba(39,39,42,0.8)',
              borderColor: shareMenuOpen ? artist.color : 'rgba(113,113,122,0.5)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            }}
            title="シェア"
          >
            <i
              className={`${shareMenuOpen ? 'ri-close-line' : 'ri-share-line'} text-sm`}
              style={{ color: shareMenuOpen ? artist.color : '#f4f4f5' }}
            />
          </motion.button>

          {/* ドロップダウンメニュー */}
          {shareMenuOpen && (
            <div
              className="absolute top-12 left-1/2 -translate-x-1/2 flex flex-col gap-2 items-center"
              style={{ animation: 'fadeInDown 0.15s ease' }}
            >
              {/* X (Twitter) */}
              <motion.a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${artist.name} - DigDim`)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                whileTap={{ scale: 0.98 }}
                className="w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 cursor-pointer"
                style={{
                  background: 'rgba(39,39,42,0.85)',
                  borderColor: 'rgba(113,113,122,0.5)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                }}
                title="Xでシェア"
              >
                <i className="ri-twitter-x-line text-sm text-zinc-100" />
              </motion.a>

              {/* LINE */}
              <motion.a
                href={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                whileTap={{ scale: 0.98 }}
                className="w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 cursor-pointer"
                style={{
                  background: 'rgba(39,39,42,0.85)',
                  borderColor: 'rgba(113,113,122,0.5)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                }}
                title="LINEでシェア"
              >
                <i className="ri-line-fill text-sm text-zinc-100" />
              </motion.a>

              {/* リンクコピー */}
              <motion.button
                onClick={handleCopyLink}
                whileTap={{ scale: 0.98 }}
                className="w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all duration-300 hover:scale-110 cursor-pointer whitespace-nowrap"
                style={{
                  background: copyDone ? `${artist.color}30` : 'rgba(39,39,42,0.85)',
                  borderColor: copyDone ? artist.color : 'rgba(113,113,122,0.5)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                }}
                title="リンクをコピー"
              >
                <i
                  className={`${copyDone ? 'ri-check-line' : 'ri-link-m'} text-sm`}
                  style={{ color: copyDone ? artist.color : '#f4f4f5' }}
                />
              </motion.button>
            </div>
          )}
        </div>
      )}

      {/* Your Depth Badge（BACKの向かい側・右上） */}
      {!loading && artist && (
        <div className="fixed top-8 right-8 z-50">
          <motion.button
            onClick={() => setShowDepthBadgePopup(true)}
            whileTap={{ scale: 0.98 }}
            className="flex flex-col items-center px-4 py-2 rounded-full backdrop-blur-sm border cursor-pointer transition-all duration-300 hover:bg-zinc-700/50 whitespace-nowrap"
            style={{
              background: 'rgba(39,39,42,0.75)',
              borderColor: 'rgba(113,113,122,0.5)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            }}
          >
            <span className="text-zinc-100 text-[10px] tracking-widest uppercase leading-tight">Your Depth</span>
            <span
              className="font-mono text-sm font-bold tracking-wider leading-tight"
              style={{ color: artist.color }}
            >
              LV. {heroProps.fanLevel}
            </span>
          </motion.button>
        </div>
      )}

      {/* Depth Popup（右上バッジ用） */}
      {showDepthBadgePopup && artist && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) setShowDepthBadgePopup(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-zinc-900/95 backdrop-blur-xl rounded-2xl p-6 max-w-md w-full border shadow-2xl"
            style={{
              borderColor: 'rgba(113,113,122,0.5)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <motion.button
              onClick={() => setShowDepthBadgePopup(false)}
              whileTap={{ scale: 0.98 }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:bg-zinc-700/50 cursor-pointer"
              style={{ background: `${artist.color}20`, color: artist.color }}
            >
              <i className="ri-close-line text-lg" />
            </motion.button>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full" style={{ background: artist.color }} />
                <p className="text-zinc-500 text-xs tracking-widest uppercase">YOUR DEPTH</p>
              </div>
              <h3 className="font-mono text-3xl font-bold tracking-wider text-zinc-100" style={{ color: artist.color }}>
                DEPTH LV. {heroProps.fanLevel}
              </h3>
              <p className="text-zinc-300 text-sm mt-1">
                次のレベルまであと{' '}
                <span className="font-mono font-bold" style={{ color: artist.color }}>
                  {heroProps.nextLevelExp - heroProps.currentExp} EXP
                </span>
              </p>
            </div>

            {/* プログレスバー */}
            <div className="mb-5">
              <div className="h-2 rounded-full overflow-hidden" style={{ background: `${artist.color}20` }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(heroProps.currentExp / heroProps.nextLevelExp) * 100}%`,
                    background: artist.color,
                  }}
                />
              </div>
            </div>

            {/* EXP内訳 */}
            <div className="mb-5 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-zinc-300">📊 現在の獲得EXP</span>
                <span className="font-mono font-bold text-zinc-100">{heroProps.currentExp} EXP</span>
              </div>
              <div className="pl-4 space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">・現場チェックイン</span>
                  <span className="font-mono text-zinc-300">{heroProps.checkInExp} EXP ({Math.floor(heroProps.checkInExp / 5)}回)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">・直接支援</span>
                  <span className="font-mono text-zinc-300">{heroProps.supportExp} EXP ({(heroProps.supportExp * 1000).toLocaleString()}円)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">・古参ボーナス</span>
                  <span className="font-mono text-zinc-300">{heroProps.hasVeteranBonus ? 'あり' : 'なし'}</span>
                </div>
              </div>
            </div>

            {/* アクションボタン */}
            <div>
              <p className="text-zinc-300 text-sm mb-3">💡 レベルを上げるには？</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: 'ri-calendar-event-line', label: 'ライブに行く' },
                  { icon: 'ri-sparkling-2-line', label: '投げ銭する' },
                  { icon: 'ri-shopping-bag-line', label: 'グッズを買う' },
                ].map((btn) => (
                  <motion.button
                    key={btn.label}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 hover:bg-zinc-700/50 cursor-pointer whitespace-nowrap"
                    style={{
                      background: `${artist.color}15`,
                      border: `1px solid ${artist.color}40`,
                      color: artist.color,
                    }}
                  >
                    <i className={btn.icon} />
                    {btn.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Mobile Layout ── */}
      <div className="lg:hidden relative z-10">
        <ArtistHeroMobile {...heroProps} bgOpacity={bgOpacity} />

        {depthDetails.fanLevel >= 1 && (
          <div className="relative border-b border-zinc-800 bg-zinc-800/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-accent/20 border-2 border-brand-accent/50">
                    <span className="text-2xl">👑</span>
                  </div>
                  <div>
                    <span className="text-brand-accent font-bold text-lg">Your Fan Level: {depthDetails.fanLevel}</span>
                    <p className="text-zinc-300 text-sm mt-0.5">
                      あなたは現在このアーティストの{' '}
                      <span className="text-brand-accent font-semibold">レベル {depthDetails.fanLevel}</span>{' '}
                      のファンです。
                    </p>
                  </div>
                </div>
                <motion.button
                  onClick={() => setIsResonateModalOpen(true)}
                  whileTap={{ scale: 0.98 }}
                  className="whitespace-nowrap px-6 py-2 rounded-full bg-brand-accent text-zinc-900 font-bold text-sm hover:bg-brand-accent/90 transition-all duration-300 cursor-pointer"
                >
                  さらに応援する
                </motion.button>
              </div>
            </div>
          </div>
        )}

        <div className="relative bg-black">
          <div
            className="absolute -top-32 left-0 right-0 h-32 pointer-events-none"
            style={{ background: 'linear-gradient(to top, black, transparent)' }}
          />
          <div className="px-6 pb-32 pt-8">
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: c02 }}
            />
            <LayerPlayer
              accentColor={c02}
              playerInfo={playerInfo}
              hasPlayerLayer={hasPlayerLayer}
              isAutoplay={isAutoplay}
              onLinkPlay={handleLinkPlay}
              compact
            />
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[500px] h-[250px] rounded-full blur-3xl opacity-[0.06] pointer-events-none"
              style={{ background: cEvents }}
            />
            <div className="mb-12">
              <ArtistEvents artistId={mockArtistId} accentColor={cEvents} />
            </div>
            <div className="relative">
              <div
                className="h-px w-full"
                style={{
                  background: `linear-gradient(to right, transparent, ${cEvents}30, ${cEchoes}50, ${cEvents}30, transparent)`,
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
                style={{ background: cEchoes, boxShadow: `0 0 20px ${cEchoes}` }}
              />
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: cEchoes }}
            />
            {isUserArtist && userId && (
              <div className="mb-12">
                <ArtistEchoes userId={userId} accentColor={cEchoes} />
              </div>
            )}
            {/* FAN DEPTH セクション（モバイル） */}
            {isUserArtist && userId && (
              <>
                <div className="relative mb-8">
                  <div
                    className="h-px w-full"
                    style={{
                      background: `linear-gradient(to right, transparent, ${cEchoes}30, ${c01}50, ${cEchoes}30, transparent)`,
                    }}
                  />
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
                    style={{ background: c01, boxShadow: `0 0 20px ${c01}` }}
                  />
                </div>
                <div className="mb-12">
                  <FanLevelProgress
                    accentColor={c01}
                    fanLevel={depthDetails.fanLevel}
                    currentExp={depthDetails.currentExp}
                    nextLevelExp={depthDetails.nextLevelExp}
                    checkInExp={depthDetails.checkInExp}
                    supportExp={depthDetails.supportExp}
                    hasVeteranBonus={depthDetails.hasVeteranBonus}
                    onResonateClick={handleResonateClick}
                    compact
                  />
                </div>
              </>
            )}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: c03 }}
            />
            <LayerCredits
              accentColor={c03}
              credits={credits}
              hasCreditsLayer={credits.length > 0}
              compact
            />
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: c04 }}
            />
            <LayerSocial
              accentColor={c04}
              socials={socials}
              hasSocialsLayer={socials.length > 0}
              artistName={artist.name}
              compact
              onSnsClick={handleSnsClick}
            />
            {isUserArtist && userId && (
              <>
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
                  style={{ background: cStrata }}
                />
                <div className="mt-16 mb-12">
                  <ResonanceStrata
                    artistId={userId}
                    accentColor={cStrata}
                    compact
                    refreshTrigger={strataRefreshTrigger}
                  />
                </div>
              </>
            )}
            {isUserArtist && userId && (
              <div className="mt-16 mb-8">
                <VagueEchoForm targetUserId={userId} targetName={artist.name} accentColor={c04} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Desktop Layout ── */}
      <div className="hidden lg:block relative z-10">
        <div className="container mx-auto px-8 py-20">
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-3xl opacity-[0.06] pointer-events-none"
            style={{ background: c01 }}
          />
          <ArtistHeroDesktop {...heroProps} />

          {depthDetails.fanLevel >= 1 && (
            <div className="relative border-b border-zinc-800 bg-zinc-800/40 mb-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand-accent/20 border-2 border-brand-accent/50">
                      <span className="text-2xl">👑</span>
                    </div>
                    <div>
                      <span className="text-brand-accent font-bold text-lg">Your Fan Level: {depthDetails.fanLevel}</span>
                      <p className="text-zinc-300 text-sm mt-0.5">
                        あなたは現在このアーティストの{' '}
                        <span className="text-brand-accent font-semibold">レベル {depthDetails.fanLevel}</span>{' '}
                        のファンです。
                      </p>
                    </div>
                  </div>
                  <motion.button
                    onClick={() => setIsResonateModalOpen(true)}
                    whileTap={{ scale: 0.98 }}
                    className="whitespace-nowrap px-6 py-2 rounded-full bg-brand-accent text-zinc-900 font-bold text-sm hover:bg-brand-accent/90 transition-all duration-300 cursor-pointer"
                  >
                    さらに応援する
                  </motion.button>
                </div>
              </div>
            </div>
          )}

          <div className="relative mb-16">
            <div
              className="h-px w-full"
              style={{
                background: `linear-gradient(to right, transparent, ${c02}30, ${cEvents}50, ${c02}30, transparent)`,
              }}
            />
            <div
              className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
              style={{ background: cEvents, boxShadow: `0 0 20px ${cEvents}` }}
            />
          </div>

          <div className="max-w-5xl mx-auto space-y-16 relative">
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: c02 }}
            />
            <LayerPlayer
              accentColor={c02}
              playerInfo={playerInfo}
              hasPlayerLayer={hasPlayerLayer}
              isAutoplay={isAutoplay}
              onLinkPlay={handleLinkPlay}
            />
            <div className="relative">
              <div
                className="h-px w-full"
                style={{
                  background: `linear-gradient(to right, transparent, ${cEvents}30, ${cEchoes}50, ${cEvents}30, transparent)`,
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
                style={{ background: cEchoes, boxShadow: `0 0 20px ${cEchoes}` }}
              />
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.06] pointer-events-none"
              style={{ background: cEvents }}
            />
            <div className="mb-12">
              <ArtistEvents artistId={mockArtistId} accentColor={cEvents} />
            </div>
            <div className="relative">
              <div
                className="h-px w-full"
                style={{
                  background: `linear-gradient(to right, transparent, ${cEvents}30, ${cEchoes}50, ${cEvents}30, transparent)`,
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
                style={{ background: cEchoes, boxShadow: `0 0 20px ${cEchoes}` }}
              />
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: cEchoes }}
            />
            {/* ECHOESセクション（デスクトップ） */}
            {isUserArtist && userId && (
              <div className="mb-12">
                <ArtistEchoes userId={userId} accentColor={cEchoes} />
              </div>
            )}
            {/* FAN DEPTH セクション（デスクトップ） */}
            {isUserArtist && userId && (
              <>
                <div className="relative">
                  <div
                    className="h-px w-full"
                    style={{
                      background: `linear-gradient(to right, transparent, ${cEchoes}30, ${c01}50, ${cEchoes}30, transparent)`,
                    }}
                  />
                  <div
                    className="absolute left-1/2 -translate-x-1/2 -top-1 w-3 h-3 rounded-full"
                    style={{ background: c01, boxShadow: `0 0 20px ${c01}` }}
                  />
                </div>
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
                  style={{ background: c01 }}
                />
                <div className="mb-12">
                  <FanLevelProgress
                    accentColor={c01}
                    fanLevel={depthDetails.fanLevel}
                    currentExp={depthDetails.currentExp}
                    nextLevelExp={depthDetails.nextLevelExp}
                    checkInExp={depthDetails.checkInExp}
                    supportExp={depthDetails.supportExp}
                    hasVeteranBonus={depthDetails.hasVeteranBonus}
                    onResonateClick={handleResonateClick}
                  />
                </div>
              </>
            )}
            <LayerCredits accentColor={c03} credits={credits} hasCreditsLayer={credits.length > 0} />
            <div
              className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: c03 }}
            />
            <LayerSocial
              accentColor={c04}
              socials={socials}
              hasSocialsLayer={socials.length > 0}
              artistName={artist.name}
              onSnsClick={handleSnsClick}
            />
            {isUserArtist && userId && (
              <>
                <div
                  className="absolute left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
                  style={{ background: cStrata }}
                />
                <div className="mt-20">
                  <VagueEchoForm targetUserId={userId} targetName={artist.name} accentColor={c04} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="h-24" />
      <BottomNav />

      {artist && (
        <ResonateModal
          isOpen={isResonateModalOpen}
          onClose={() => setIsResonateModalOpen(false)}
          artistId={isMockArtist ? String(id) : userId || ''}
          artistName={artist.name}
          displayColor={artist.color}
          onSuccess={handleResonateSuccess}
        />
      )}

      {/* ページ訪問時のレベルアップオーバーレイ（既存） */}
      {showLevelUp && artist && (
        <LevelUpOverlay
          newLevel={levelUpNewLevel}
          accentColor={artist.color}
          artistName={artist.name}
          onComplete={() => setShowLevelUp(false)}
        />
      )}

      {/* RESONATE直後のレベルアップオーバーレイ（新規） */}
      {resonateLevelUpInfo && artist && (
        <LevelUpOverlay
          newLevel={resonateLevelUpInfo.newLevel}
          accentColor={resonateLevelUpInfo.accentColor}
          artistName={resonateLevelUpInfo.artistName}
          onComplete={clearLevelUp}
        />
      )}
    </div>
  );
}
